import React from 'react';
import { Icon } from '@/modules/Icon';
import { Avatar } from '@/modules/Avatar';
import { InnerButton } from '@/modules/InnerButton';
import { useAlertStyles } from '@/modules/Alert/hooks/useAlertStyles';
import { AlertHeaderProps } from '@/shared/types/alert';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

/**
 * AlertHeader - Cabecera del Alert
 *
 * Tokens semánticos de Figma:
 * - Gap: 8px (space_8) entre icono y contenido
 * - Título: font-body-md-bold
 * - Mensaje: font-body-xs-regular
 * - Icono: 20px
 */

export const AlertHeader = React.memo((props: AlertHeaderProps) => {
  const { getIconForType } = useAlertStyles();

  const mustBeWhite = props.variant === 'contained';
  const isOutlined = props.variant === 'outlined';

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (props.onClose) {
      props.onClose(e);
    }
  };

  const handlePrimaryClick = () => {
    if (props.onPrimaryClick) {
      props.onPrimaryClick();
    }
  };

  const handleSecondaryClick = () => {
    if (props.onSecondaryClick) {
      props.onSecondaryClick();
    }
  };

  return (
    <div
      className={`ui-flex ui-w-full ui-flex-col ui-justify-between ui-gap-16 ${props.title ? 'ui-items-start' : 'ui-items-start'}`}
    >
      {/* Avatar o Icono - 20px según Figma */}
      <div className="ui-flex ui-w-full ui-flex-1 ui-items-start ui-justify-between ui-gap-8">
        {props.avatar ? (
          // Avatar con imagen de persona
          <Avatar type="people" src={props.avatar} size="xs" className="ui-flex-shrink-0" />
        ) : isOutlined ? (
          // Icono con fondo circular para variante outlined (comportamiento anterior)
          <Icon
            name={getIconForType(props.color || 'danger')}
            color={props.color}
            size="sm"
            className="ui-flex-shrink-0"
            background
            backgroundColor={props.color || 'danger'}
            backgroundSize="sm"
          />
        ) : (
          // Icono fijo según el color del alert
          // Wrapper que usa la variable CSS del color de texto de alerta
          <div
            className="ui-flex-shrink-0"
            style={{
              color: `var(--ui-color-alert-${props.color || 'danger'}-text)`,
            }}
          >
            <Icon name={getIconForType(props.color || 'danger')} color="current" size="sm" />
          </div>
        )}
        <div className="ui-flex ui-flex-1 ui-items-start ui-justify-between ui-gap-8">
          {/* Contenido de texto */}
          <div className="ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-gap-0">
            {/* Título - font-body-md-bold */}
            {props.title !== null && (
              <div
                className={`ui-text-body-sm-bold ui-font-body-sm-bold ui-leading-body-sm-bold ui-tracking-body-sm ${
                  isOutlined ? 'ui-text-primary' : ''
                }`}
              >
                {props.title}
              </div>
            )}
            {/* Mensaje - font-body-xs-regular */}
            <div
              className={`ui-text-pretty ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs ${
                isOutlined ? 'ui-text-secondary' : ''
              }`}
            >
              {props.message}
            </div>

            {props.description !== null && (
              <div
                className={`ui-mt-2 ui-text-pretty ui-text-body-xs-medium ui-font-body-xs-medium ui-leading-body-xs-medium ui-tracking-body-xs ${
                  isOutlined ? 'ui-text-primary' : ''
                }`}
              >
                {props.description}
              </div>
            )}
          </div>

          {/* Botón de cerrar */}
          {props.showCloseButton && (
            <InnerButton
              icon={faXmark}
              size="xxs"
              pill="pill"
              color="tertiary"
              className="ui-flex-shrink-0"
              onClick={handleClose}
              aria-label="Cerrar alerta"
            />
          )}
        </div>
      </div>
      {(props.onSecondaryClick || props.onPrimaryClick) && (
        <div className="ui-flex ui-w-full ui-items-center ui-justify-end ui-gap-1.5">
          {props.onSecondaryClick && (
            <InnerButton
              clickable={true}
              theme={'solid'}
              color={'tertiary'}
              size="xs"
              onClick={handleSecondaryClick}
            >
              {props.secondaryButtonText || ''}
            </InnerButton>
          )}
          {props.onPrimaryClick && (
            <InnerButton
              clickable={true}
              theme={'solid'}
              color={'secondary'}
              size="xs"
              onClick={handlePrimaryClick}
            >
              {props.primaryButtonText || ''}
            </InnerButton>
          )}
        </div>
      )}
    </div>
  );
});

AlertHeader.displayName = 'AlertHeader';
